import { useEffect, useState } from "react";
import {
    fetchCategories,
    getRankingDraft,
    takeOverResults,
    updateRankingEntry,
    publishRanking,
    unpublishRanking,
    saveManualWinner,
    type Category,
    type RankingDraftRow,
} from "../api/tcg";

type ManualWinnerForm = {
    display_text: string;
    game: string;
    image_url: string;
    description: string;
};

const EMPTY_MANUAL_WINNER: ManualWinnerForm = {
    display_text: "",
    game: "",
    image_url: "",
    description: "",
};

function isCommunityVotingCategory(category: Category | undefined): boolean {
    return category?.group_key === "product" || category?.group_key === "community";
}

function AdminResults({ token }: { token: string }) {
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [rows, setRows] = useState<RankingDraftRow[]>([]);
    const [manualWinner, setManualWinner] = useState<ManualWinnerForm>(EMPTY_MANUAL_WINNER);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        fetchCategories()
            .then((cats) => {
                setCategories(cats);
                if (cats.length > 0) setCategoryId(cats[0].id);
            })
            .catch((e) => setError(e instanceof Error ? e.message : "Fehler."));
    }, []);

    async function loadDraft(id: number) {
        setError(null);
        try {
            setRows(await getRankingDraft(token, id));
        } catch (e) {
            setError(e instanceof Error ? e.message : "Konnte Entwurf nicht laden.");
            setRows([]);
        }
    }

    useEffect(() => {
        if (categoryId !== null) {
            setMessage(null);
            setRows([]);
            loadDraft(categoryId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId]);

    const selectedCategory = categories.find((c) => c.id === categoryId);
    const isManualCategory = selectedCategory ? !isCommunityVotingCategory(selectedCategory) : false;
    const published = rows.length > 0 && rows.every((r) => r.published);

    useEffect(() => {
        if (!isManualCategory) {
            setManualWinner(EMPTY_MANUAL_WINNER);
            return;
        }

        const winner = rows.find((r) => r.rank === 1);
        setManualWinner({
            display_text: winner?.display_text ?? "",
            game: winner?.game ?? "",
            image_url: winner?.image_url ?? "",
            description: winner?.description ?? "",
        });
    }, [isManualCategory, rows]);

    async function handleTakeOver() {
        if (categoryId === null) return;
        setLoading(true);
        setError(null);
        setMessage(null);
        try {
            setRows(await takeOverResults(token, categoryId));
            setMessage("Ergebnisse übernommen (Entwurf - noch nicht öffentlich).");
        } catch (e) {
            setError(e instanceof Error ? e.message : "Übernehmen fehlgeschlagen.");
        } finally {
            setLoading(false);
        }
    }

    function setField(rank: number, field: "game" | "image_url", value: string) {
        setRows((prev) => prev.map((r) => (r.rank === rank ? { ...r, [field]: value } : r)));
    }

    function setManualField(field: keyof ManualWinnerForm, value: string) {
        setManualWinner((prev) => ({ ...prev, [field]: value }));
    }

    async function handleSaveRow(rank: number) {
        if (categoryId === null) return;
        const row = rows.find((r) => r.rank === rank);
        if (!row) return;
        setError(null);
        setMessage(null);
        try {
            await updateRankingEntry(token, categoryId, rank, row.game ?? "", row.image_url ?? "");
            setMessage(`Platz ${rank} gespeichert.`);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
        }
    }

    async function handleSaveManualWinner() {
        if (categoryId === null) return;
        setError(null);
        setMessage(null);

        const winnerName = manualWinner.display_text.trim();
        if (!winnerName) {
            setError("Bitte gib einen Gewinner ein.");
            return;
        }

        setLoading(true);
        try {
            const saved = await saveManualWinner(token, categoryId, {
                display_text: winnerName,
                game: manualWinner.game.trim(),
                image_url: manualWinner.image_url.trim(),
                description: manualWinner.description.trim(),
            });
            setRows([saved]);
            setMessage("Gewinner gespeichert.");
        } catch (e) {
            setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
        } finally {
            setLoading(false);
        }
    }

    async function handlePublish() {
        if (categoryId === null) return;
        setError(null);
        setMessage(null);
        try {
            await publishRanking(token, categoryId);
            await loadDraft(categoryId);
            setMessage("Ergebnisse veröffentlicht.");
        } catch (e) {
            setError(e instanceof Error ? e.message : "Veröffentlichen fehlgeschlagen.");
        }
    }

    async function handleUnpublish() {
        if (categoryId === null) return;
        setError(null);
        setMessage(null);
        try {
            await unpublishRanking(token, categoryId);
            await loadDraft(categoryId);
            setMessage("Veröffentlichung zurückgezogen.");
        } catch (e) {
            setError(e instanceof Error ? e.message : "Zurueckziehen fehlgeschlagen.");
        }
    }

    return (
        <section className="admin-card">
            <h2 className="admin-card-title">Ergebnisse veröffentlichen</h2>

            <div className="admin-control-row">
                <label className="admin-label">Kategorie</label>
                <select
                    className="admin-input"
                    value={categoryId ?? ""}
                    onChange={(e) => setCategoryId(Number(e.target.value))}
                >
                    {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>

            {!isManualCategory && (
                <div className="admin-actions">
                    <button className="admin-btn-primary" onClick={handleTakeOver} disabled={loading || categoryId === null}>
                        {loading ? "Übernehme..." : "Ergebnisse übernehmen"}
                    </button>
                </div>
            )}

            {error && <p className="admin-error">{error}</p>}
            {message && <p className="admin-note">{message}</p>}

            {isManualCategory && (
                <div className="admin-action-block">
                    <h3 className="admin-subtitle">Gewinner eintragen</h3>
                    <p className="admin-note" style={{ marginBottom: "16px" }}>
                        Diese Kategorie wird nicht ueber Community-Voting entschieden. Hier wird nur Platz 1 gepflegt.
                    </p>

                    <label className="admin-label">Gewinner</label>
                    <input
                        className="admin-input"
                        type="text"
                        value={manualWinner.display_text}
                        onChange={(e) => setManualField("display_text", e.target.value)}
                        placeholder="Name des Gewinners"
                    />

                    <label className="admin-label">Game / Kontext</label>
                    <input
                        className="admin-input"
                        type="text"
                        value={manualWinner.game}
                        onChange={(e) => setManualField("game", e.target.value)}
                        placeholder="z. B. Spielname, Unternehmen oder Projekt"
                    />

                    <label className="admin-label">Bild-URL</label>
                    <input
                        className="admin-input"
                        type="text"
                        value={manualWinner.image_url}
                        onChange={(e) => setManualField("image_url", e.target.value)}
                        placeholder="https://..."
                    />

                    <label className="admin-label">Siegerbeschreibung</label>
                    <textarea
                        className="admin-input"
                        rows={4}
                        value={manualWinner.description}
                        onChange={(e) => setManualField("description", e.target.value)}
                        placeholder="Kurzer Text, warum dieser Gewinner ausgezeichnet wurde."
                    />

                    <button
                        className="admin-btn-primary"
                        onClick={handleSaveManualWinner}
                        disabled={loading || categoryId === null || !manualWinner.display_text.trim()}
                    >
                        Speichern
                    </button>
                </div>
            )}

            {rows.length > 0 && (
                <>
                    <p className="admin-note">
                        Status: {published ? "veröffentlicht (öffentlich sichtbar)" : "Entwurf - nicht öffentlich"}
                    </p>

                    {!isManualCategory && (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Platz</th>
                                    <th>Name</th>
                                    <th>Stimmen</th>
                                    <th>Game</th>
                                    <th>Bild-URL</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.rank}>
                                        <td>{r.rank}</td>
                                        <td>{r.display_text}</td>
                                        <td>{r.points}</td>
                                        <td>
                                            <input
                                                className="admin-input"
                                                value={r.game ?? ""}
                                                onChange={(e) => setField(r.rank, "game", e.target.value)}
                                                placeholder="z. B. Spielname"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="admin-input"
                                                value={r.image_url ?? ""}
                                                onChange={(e) => setField(r.rank, "image_url", e.target.value)}
                                                placeholder="https://..."
                                            />
                                        </td>
                                        <td>
                                            <button className="admin-btn-secondary" onClick={() => handleSaveRow(r.rank)}>
                                                Speichern
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <div className="admin-actions">
                        {published ? (
                            <button className="admin-btn-secondary" onClick={handleUnpublish}>
                                Zurückziehen
                            </button>
                        ) : (
                            <button className="admin-btn-primary" onClick={handlePublish}>
                                Veröffentlichen
                            </button>
                        )}
                    </div>
                </>
            )}
        </section>
    );
}

export default AdminResults;
